// internal-deps
import {
    HTTP_METHODS,
    makeDownstreamApiCallDeprecated,
    IDineoutLocalCart as ILocalCart,
    addAdditionalHeadersToPLAPI,
} from "@swiggy-private/checkout-core";
import { ApiResponse, TEXTS } from "@swiggy-private/core-utils";
import { Err, Ok, Result } from "@swiggy-private/js-fp";
import { format } from "@swiggy-private/js-utils";

// constants
import { DOWNSTREAM_ENDPOINTS, DOWNSTREAM_HOSTS } from "@dineout-cart-client/constants";

// interfaces
import { IDineoutCartResponse } from "@dineout-cart-client/interfaces";
import { ICouponsAPIResponse } from "@dineout-cart-client/interfaces/Coupon";

async function applyCoupon(localCart: ILocalCart): Promise<IDineoutCartResponse> {
    const endPoint = format(
        DOWNSTREAM_ENDPOINTS.APPLY_COUPON_ENDPOINT,
        localCart?.cartMetaData?.couponCode,
        localCart.cartType,
    );
    const response = (await makeDownstreamApiCallDeprecated({
        apiHost: DOWNSTREAM_HOSTS.PAYMENTS_HOST,
        endpoint: endPoint,
        options: {
            method: HTTP_METHODS.POST,
            body: JSON.stringify(localCart),
        },
    })) as IDineoutCartResponse;

    return response;
}

export function applyCouponWrapper(localCart: ILocalCart): Promise<Result<IDineoutCartResponse, Error | ApiResponse>> {
    return applyCoupon(localCart)
        .then((cartResponse) => {
            // This is required because we need to store complete response in the cart context to handle steal deal cases
            // for which we need to remove the root level api check for unwrapResponseWithStatusCode
            // TODO: remove this post proper testing and handling is done for non-zero error cases
            if (cartResponse?.statusCode !== 0) {
                throw {
                    ...cartResponse,
                    statusMessage: cartResponse?.statusMessage ?? TEXTS.OOPS_SOMETHING_WENT_WRONG,
                };
            }
            return Ok(cartResponse);
        })
        .catch((err) => {
            return Err(err);
        });
}

interface IFetchCouponsRequest {
    lat: string;
    lng: string;
    resId: string;
    cartValue: number;
    dealCategory: string;
    entityId?: string;
    isPartPaySelected?: boolean;
    tableId?: string;
}

async function fetchCoupons({
    lat,
    lng,
    resId,
    cartValue,
    dealCategory,
    entityId,
    isPartPaySelected = false,
    tableId = "",
}: IFetchCouponsRequest): Promise<ICouponsAPIResponse> {
    const endpoint = format(
        DOWNSTREAM_ENDPOINTS.FETCH_COUPONS,
        lat,
        lng,
        resId,
        cartValue,
        dealCategory,
        entityId,
        isPartPaySelected,
        tableId,
    );

    const response = (await makeDownstreamApiCallDeprecated(
        {
            apiHost: DOWNSTREAM_HOSTS.GANDALF_HOST,
            endpoint,
            options: {
                method: HTTP_METHODS.GET,
                headers: addAdditionalHeadersToPLAPI(),
            },
        },
        true,
    )) as ICouponsAPIResponse;
    return response;
}

export function fetchCouponsWrapper(
    props: IFetchCouponsRequest,
): Promise<Result<ICouponsAPIResponse, Error | ApiResponse>> {
    return fetchCoupons(props)
        .then((couponsResponse) => {
            return Ok(couponsResponse);
        })
        .catch((err) => {
            return Err(err);
        });
}
